// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useEffect, useCallback, useMemo, useRef } from 'react';

import { type Facility, type FacilityStatusFilter, useEvolvePaginatedGet } from '@evolvemep/api-ts';
import type { RowSelectionOptions } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';

import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { ActiveAllSelect } from 'components/Mantine/GlobalFilters/ActiveAllSelect';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { TABLE_VIEW_TYPE } from 'helpers/ag-grid/TableViewPanel/constants';
import { useServerSideGrid } from 'helpers/ag-grid/useServerSideGrid';
import type { UseState } from 'helpers/helper-types';
import { isNotNil } from 'helpers/isNotNil';
import { useActiveAllFilterState } from 'hooks/useActiveAllFilterState';
import { getFacilitiesColDef } from 'modules/Admin/Facilities/columnDefs';
import { CreateFacilityModal } from 'modules/Admin/Facilities/CreateFacilityModal';
import { FacilityActionModals, type FacilityActionModalState } from 'modules/Admin/Facilities/FacilityActionModals';

type Props = {
  facilityAction: FacilityActionModalState | null;
  setFacilityAction: UseState<FacilityActionModalState | null>;
  selectFacility: (facility: Facility | null) => void;
  selectedFacility: Facility | null;
  editing: boolean;
};

const rowSelection: RowSelectionOptions = {
  mode: 'singleRow',
  enableClickSelection: false,
  checkboxes: false,
};
const ACTIVE_FACILITY_STATUSES: FacilityStatusFilter[] = ['Active'];
const ALL_FACILITY_STATUSES: FacilityStatusFilter[] = ['All'];

export const FacilitiesTable = ({
  facilityAction,
  setFacilityAction,
  selectFacility,
  selectedFacility,
  editing,
}: Props) => {
  const [facilityStatus, selectProps] = useActiveAllFilterState(ACTIVE_FACILITY_STATUSES, ALL_FACILITY_STATUSES);

  const { fetchPage, abortController, setDefaultOpts } = useEvolvePaginatedGet('shop/facility');
  useEffect(() => {
    setDefaultOpts({
      defaultConfig: {
        params: {
          status: facilityStatus[0],
        },
      },
    });
  }, [setDefaultOpts, facilityStatus]);

  const colDef = useMemo(
    () =>
      getFacilitiesColDef({
        actionFacility: setFacilityAction,
      }),
    [setFacilityAction],
  );

  const { agGridProps, gridApi } = useServerSideGrid({
    tableName: 'facilities',
    tableViewType: TABLE_VIEW_TYPE.FACILITIES,
    exportFileName: 'Facilities',
    fetchPage,
    abortController,
    colDef,
    rowId: 'facilityId',
  });

  const needsRefresh = useRef(false);
  useEffect(() => {
    // Refresh the grid when the user stops editing
    if (!editing) {
      if (needsRefresh.current) {
        refreshGrid(gridApi);
      }
      needsRefresh.current = false;
    } else {
      needsRefresh.current = true;
    }
  }, [editing, gridApi]);

  return (
    <>
      <BasePageHeader
        title="Facilities"
        topRightComponent={
          <>
            <ActiveAllSelect {...selectProps} dataLabel="Facilities" withLabel={false} />
            <CreateFacilityModal
              editing={editing}
              onFacilityCreated={(f) => {
                selectFacility(f);
                refreshGrid(gridApi);
              }}
            />
          </>
        }
      />
      <div style={{ height: '100%' }}>
        <AgGridReact<Facility>
          {...agGridProps}
          rowSelection={rowSelection}
          onSelectionChanged={(e) => {
            const selected = e.api.getSelectedNodes()[0];
            if (isNotNil(selected) && isNotNil(selected.data)) {
              selectFacility(selected.data);
            }
          }}
          onModelUpdated={(e) => {
            if (isNotNil(selectedFacility)) {
              e.api.getRowNode(selectedFacility.facilityId)?.setSelected(true, true);
            }
          }}
        />
      </div>
      <FacilityActionModals
        facilityActionState={facilityAction}
        onClose={useCallback(() => setFacilityAction(null), [setFacilityAction])}
        selectFacility={selectFacility}
        refresh={() => refreshGrid(gridApi)}
      />
    </>
  );
};
